import { inject, Injectable, signal } from '@angular/core';
import { AudioService } from '@softline/application';
import { LogoutService, UserInfoService } from '@softline/auth';
import {
  ConnectionError,
  ForbiddenError,
  NetworkError,
  NotFoundError,
  ServerError,
  TimeoutError,
  UnauthorizedError,
  UnknownError,
  UnknownResponseError,
} from '@softline/core';
import { FormatPipe, MessageBarService } from '@softline/ui-core';
import { StempelApiRepository } from '../repositories/stempel-api.repository';
import { StempelIndexedDbRepository } from '../repositories/stempel-indexed-db.repository';
import { StempelBody, TimeClockExtras } from '../types/stempel-body';
import { StempelResult } from '../types/stempel-result';

@Injectable()
export class StempelService {
  private readonly formatPipe = inject(FormatPipe);

  private readonly apiRepository = inject(StempelApiRepository);
  private readonly indexedDbRepository = inject(StempelIndexedDbRepository);

  // private readonly networkStatusService = inject(NetworkStatusService);
  private readonly messageBarService = inject(MessageBarService);
  private readonly audioService = inject(AudioService);

  private readonly logoutService = inject(LogoutService);
  private readonly userInfo = inject(UserInfoService);

  private savingOfflineRecords = false;
  private timeout: ReturnType<typeof setTimeout> | null = null;

  public readonly message = signal<(StempelResult & { message: string }) | null>(null);

  async save(
    serialNumber: string,
    time: string,
    extraData?: TimeClockExtras
  ): Promise<void> {
    let result: StempelResult;
    if (!this.userInfo.value()?.userId)
      result = await this.saveOffline(serialNumber, time, extraData);
    else result = await this.saveOnline(serialNumber, time, extraData);
    this.audioService.play('notify');
    await this.setMessage(result);
  }

  private async saveOnline(
    serialNumber: string,
    time: string,
    extraData?: TimeClockExtras
  ): Promise<StempelResult> {
    const record: StempelBody = {
      chipid: serialNumber,
      kzonline: true,
      time: time,
      bemerkung: extraData?.bemerkung ?? '',
      idcode: extraData?.idcode,
      chipidCheck: extraData?.chipidCheck,
    };
    try {
      let records = [record];
      const offlineRecords = await this.indexedDbRepository.loadAll();

      const includeOffline = !this.savingOfflineRecords && offlineRecords?.length > 0;
      if (includeOffline) {
        this.savingOfflineRecords = true;
        records = [...offlineRecords, ...records];
      }

      const result = await this.apiRepository.save(records);
      if (includeOffline) {
        await this.indexedDbRepository.clear();
        this.messageBarService.success(
          'Offline Stempel wurden erfolgreich synchronisiert',
          'Synchronisierung Erfolgreich'
        );
        this.savingOfflineRecords = false;
      }
      return {
        ...result,
        kzonline: true,
      };
    } catch (e: unknown) {
      this.savingOfflineRecords = false;

      if (
        e instanceof ConnectionError ||
        e instanceof NetworkError ||
        e instanceof UnauthorizedError ||
        e instanceof ForbiddenError ||
        e instanceof NotFoundError ||
        e instanceof ServerError ||
        e instanceof UnknownResponseError ||
        e instanceof UnknownError ||
        e instanceof TimeoutError
      ) {
        const result = await this.saveOffline(serialNumber, time, extraData);
        if (e instanceof UnauthorizedError) await this.logoutService.logout();
        return result;
      } else {
        this.audioService.play('warning');
        throw e;
      }
    }
  }

  private async saveOffline(
    serialNumber: string,
    time: string,
    extraData?: TimeClockExtras
  ): Promise<StempelResult> {
    const record: StempelBody = {
      chipid: serialNumber,
      kzonline: false,
      time: time,
      bemerkung: extraData?.bemerkung ?? '',
      idcode: extraData?.idcode,
      chipidCheck: extraData?.chipidCheck,
    };
    await this.indexedDbRepository.add(record);
    return {
      lstfix: null,
      kzonline: false,
      stempel: {
        datum: time,
        erfzeit: time,
      },
    };
  }

  private async saveOfflineRecords(): Promise<void> {
    this.savingOfflineRecords = true;
    try {
      const offlineRecords = await this.indexedDbRepository.loadAll();
      if (offlineRecords.length === 0) {
        this.savingOfflineRecords = false;
        return;
      }

      await this.apiRepository.save(offlineRecords);
      await this.indexedDbRepository.clear();
      this.messageBarService.success(
        'Offline Stempel wurden erfolgreich synchronisiert',
        'Synchronisierung Erfolgreich'
      );
      this.savingOfflineRecords = false;
    } catch (e) {
      console.warn('[StempelService] Error writing offline records to api', e);
      this.savingOfflineRecords = false;
    }
  }

  private async setMessage(result: StempelResult): Promise<void> {
    const formattedTime = this.formatPipe.transform(
      result?.stempel?.erfzeit,
      'TIME.DEFAULT'
    );
    const formattedDate = this.formatPipe.transform(
      result?.stempel?.datum,
      'DATE.DEFAULT'
    );
    let message = `Zeitstempel: ${formattedTime} (${formattedDate})`;

    if (result?.lstfix) {
      message = `${result.lstfix.persnr} - ${result.lstfix.vorname} ${result.lstfix.zuname}, ${message}`;
    }

    if (result.stempel?.pmscode) {
      message += `, Code: ${result.stempel.pmscode}`;
    }

    if (result.stempel?.bemerkung && result.stempel.bemerkung?.length > 0) {
      message += `, Bemerkung: ${result.stempel?.bemerkung}`;
    }

    if (!result?.kzonline)
      message += ' - abgleich erflogt bei Stempel mit Netzwerkverbindung';

    if (this.timeout) {
      this.message.set(null);
      window.clearTimeout(this.timeout);
      this.timeout = null;
      await new Promise<void>((resolve) => setTimeout(() => resolve(), 150));
    }

    this.message.set({ ...result, message });
    this.timeout = setTimeout(() => {
      this.message.set(null);
      this.timeout = null;
    }, 4000);
  }
}
